import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PricingSection = () => {
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState('monthly');

  const plans = [
    {
      name: 'Básico',
      description: 'Perfeito para começar',
      monthlyPrice: 'R$ 49,90',
      yearlyPrice: 'R$ 499,00',
      features: [
        'Agenda básica',
        'Suporte por email',
        'Página de agendamento',
        'Sem limite de registros'
      ],
      limitations: [
        'Sem relatórios avançados',
      ],
      recommended: false,
      variant: 'outline',
    },
    {
      name: 'Profissional',
      description: 'Mais popular entre psicólogos',
      monthlyPrice: 'R$ 99,90',
      yearlyPrice: 'R$ 999,00',
      features: [
        'Pacientes ilimitados',
        'Agenda completa',
        'WhatsApp automático',
        'Relatórios detalhados',
        'Suporte prioritário',
        'Página personalizada',
        'Notificações configuráveis',
      ],
      limitations: [],
      recommended: true,
      variant: 'default',
    },
    {
      name: 'Clínica',
      description: 'Para múltiplos profissionais',
      monthlyPrice: 'R$ 199,90',
      yearlyPrice: 'R$ 1.999,00',
      features: [
        'Tudo do Profissional',
        'Até 5 psicólogos',
        'Gestão centralizada',
        'API de integração',
        'Suporte dedicado',
        'Treinamento incluído',
        'Relatórios consolidados',
      ],
      limitations: [],
      recommended: false,
      variant: 'outline',
    },
  ];

  const handleSelectPlan = (planName) => {
    navigate('/authentication-hub', { state: { selectedPlan: planName } });
  };

  return (
    <section id="pricing" className="py-20 px-6 bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full mb-4">
            <Icon name="CreditCard" size={16} color="var(--color-primary)" />
            <span className="text-sm font-medium text-primary">
              Planos Transparentes
            </span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-foreground mb-4">
            Escolha o plano ideal para você
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
            Sem taxas ocultas. Cancele quando quiser. Teste grátis por 14 dias.
          </p>

          <div className="inline-flex items-center gap-2 p-1 bg-muted rounded-lg">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-6 py-2 rounded-md text-sm font-medium transition-all ${
                billingCycle === 'monthly' ?'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Mensal
            </button>
            <button
              onClick={() => setBillingCycle('yearly')}
              className={`px-6 py-2 rounded-md text-sm font-medium transition-all ${
                billingCycle === 'yearly' ?'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Anual
              <span className="ml-2 text-xs text-success font-semibold">
                -17%
              </span>
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {plans?.map((plan, index) => (
            <div
              key={index}
              className={`relative bg-card rounded-2xl p-8 border transition-all duration-300 hover:shadow-elevated ${
                plan?.recommended
                  ? 'border-primary shadow-lg scale-105'
                  : 'border-border hover:-translate-y-1'
              }`}
            >
              {plan?.recommended && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-primary text-primary-foreground text-sm font-semibold rounded-full shadow-lg">
                  Mais Popular
                </div>
              )}

              <div className="mb-6">
                <h3 className="text-2xl font-bold text-foreground mb-2">
                  {plan?.name}
                </h3>
                <p className="text-muted-foreground">{plan?.description}</p>
              </div>

              <div className="mb-6">
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-bold text-foreground">
                    {billingCycle === 'monthly'
                      ? plan?.monthlyPrice
                      : plan?.yearlyPrice}
                  </span>
                  <span className="text-muted-foreground">
                    /{billingCycle === 'monthly' ? 'mês' : 'ano'}
                  </span>
                </div>
                {billingCycle === 'yearly' && (
                  <p className="text-sm text-success mt-2">
                    Economize R$ {(parseFloat(plan?.monthlyPrice?.replace('R$ ', '')?.replace(',', '.')) * 12 - parseFloat(plan?.yearlyPrice?.replace('R$ ', '')?.replace(',', '.')))?.toFixed(2)?.replace('.', ',')} por ano
                  </p>
                )}
              </div>

              <Button
                variant={plan?.variant}
                size="lg"
                fullWidth
                onClick={() => handleSelectPlan(plan?.name)}
                className="mb-6"
              >
                Começar Agora
              </Button>

              <div className="space-y-4">
                <p className="text-sm font-semibold text-foreground">
                  Recursos incluídos:
                </p>
                {plan?.features?.map((feature, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <Icon
                      name="Check"
                      size={20}
                      color="var(--color-success)"
                      className="flex-shrink-0 mt-0.5"
                    />
                    <span className="text-sm text-muted-foreground">
                      {feature}
                    </span>
                  </div>
                ))}
                {plan?.limitations?.map((limitation, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <Icon
                      name="X"
                      size={20}
                      color="var(--color-muted-foreground)"
                      className="flex-shrink-0 mt-0.5"
                    />
                    <span className="text-sm text-muted-foreground line-through">
                      {limitation}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <p className="text-muted-foreground mb-4">
            Precisa de um plano personalizado para sua clínica?
          </p>
          <Button variant="outline" iconName="Mail" iconPosition="left">
            Falar com Vendas
          </Button>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;